import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, Divider } from "@mui/material";
import localesKeys from "../../../locales/localesKeys.json";

import styles from "./Footer.module.scss";

import { PrimaryButton, SocialShare } from "../../common";
import { axiosInstance } from "../../../services";
import { slugify } from "../../../utils";
import { useInfo } from "../../../context/InfoContext";

const Footer = () => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const { data: userInfo } = useInfo();
  const { socialMediaLinks } = userInfo || {};

  const currentYear = new Date().getFullYear();
  const [list, setList] = useState([]);
  const [message, setMessage] = useState("");

  const footerLinks = useMemo(() => {
    return {
      about: list,
      resources: [
        { label: t(localesKeys?.FOOTER_RESOURCELIST_GRANTH), path: "/granth" },
        { label: t(localesKeys?.FOOTER_RESOURCELIST_TIRTH), path: "/tirth" },
        { label: t(localesKeys?.FOOTER_RESOURCELIST_BLOGS), path: "/blogs" },
      ],
      support: [
        { label: t(localesKeys?.FOOTER_SUPPORTLIST_CONTACT), path: "/contact" },
        {
          label: t(localesKeys?.FOOTER_SUPPORTLIST_PRIVACY),
          path: "/privacy-policy",
        },
        {
          label: t(localesKeys?.FOOTER_SUPPORTLIST_SERVICES),
          path: "/terms-of-service",
        },
      ],
    };
  }, [list, t]);

  useEffect(() => {
    if (!list?.length) {
      axiosInstance.get("/aboutus").then((res) => {
        if (res?.data?.length) {
          let sortedList = res?.data?.sort((a, b) =>
            a.order < b.order ? -1 : 1
          );
          sortedList = sortedList.map((item) => ({
            label: item.heading,
            path: `/about#${slugify(item.heading?.[language])}`,
          }));
          setList(sortedList);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    if (email) {
      axiosInstance
        .post("/subscribers", { email: email })
        .then((res) => {
          if (res?.status === "success") {
            setMessage({ type: "success", message: res?.message });
            e.target.reset();
          }
        })
        .catch((err) => {
          setMessage({
            type: "error",
            message: err?.response?.data?.message
              ? err?.response?.data?.message
              : err?.message,
          });
        });
    }
  };

  useEffect(() => {
    if (message && message?.type) {
      setTimeout(() => {
        setMessage("");
      }, 3000);
    }
  }, [message]);
  return (
    <footer className={styles.footer}>
      <Box className={styles.footerContent}>
        {/* Main Footer Content */}
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box className={styles.brandSection}>
              <Link to="/" className={styles.logo}>
                <img
                  src={userInfo?.logoUrl || "https://res.cloudinary.com/daojypjrh/image/upload/logo.jpg"}
                  alt={userInfo?.title || "JainGlory Foundation"}
                  className={styles.logoImage}
                  loading="lazy"
                />
              </Link>
              <Typography variant="body1" className={styles.description}>
                {t(localesKeys?.FOOTER_DESCRIPTION)}
              </Typography>
              <Box className={styles.socialLinks}>
                <SocialShare
                  socialMediaLinks={socialMediaLinks}
                  title=""
                  wrapperClass={styles.wrapperSocial}
                  isSocialPage={true}
                />
              </Box>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={4}>
                <Typography variant="h6" className={styles.sectionTitle}>
                  {t(localesKeys?.FOOTER_LISTHEAD_ABOUT)}
                </Typography>
                <ul className={styles.linkList}>
                  {footerLinks?.about.map((link, index) => (
                    <li key={index}>
                      <Link to={link.path} className={styles.link}>
                        {link.label?.[language]}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="h6" className={styles.sectionTitle}>
                  {t(localesKeys?.FOOTER_LISTHEAD_RESOURCES)}
                </Typography>
                <ul className={styles.linkList}>
                  {footerLinks.resources.map((link, index) => (
                    <li key={index}>
                      <Link to={link.path} className={styles.link}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="h6" className={styles.sectionTitle}>
                  {t(localesKeys?.FOOTER_LISTHEAD_SUPPORT)}
                </Typography>
                <ul className={styles.linkList}>
                  {footerLinks.support.map((link, index) => (
                    <li key={index}>
                      <Link to={link.path} className={styles.link}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Newsletter Section */}
        <Box className={styles.newsletterSection}>
          <Typography variant="h6" className={styles.newsletterTitle}>
            {t(localesKeys?.FOOTER_SUBSCRIBE_MSSG)}
          </Typography>
          <form
            className={styles.newsletterForm}
            onSubmit={(e) => {
              handleSubmit(e);
            }}
          >
            <input
              type="email"
              name="email"
              required={true}
              placeholder={t(localesKeys?.FOOTER_SUBSCRIBE_PLACEHOLDER)}
              className={styles.newsletterInput}
            />
            <PrimaryButton
              type="submit"
              onClick={() => {}} // Replace with actual subscription logic
              title={t(localesKeys?.FOOTER_SUBSCRIBE_BUTTON)}
              className={styles.subscribeBtn}
            />
          </form>
          {message && message?.type ? (
            <p
              className={
                styles.message +
                " " +
                (message?.type === "success" ? styles.success : styles.error)
              }
            >
              {message?.message}
            </p>
          ) : null}
        </Box>

        {/* Bottom Bar */}
        <Divider className={styles.divider} />
        <Box className={styles.bottomBar}>
          <Typography variant="body2" className={styles.copyright}>
            {t(localesKeys?.FOOTER_COPYRIGHT, { year: currentYear, orgName: userInfo?.title || "JainGlory Foundation" })}
          </Typography>
          <Box className={styles.poweredByContainer}>
            <Typography variant="body2" className={styles.poweredByText}>
              {t(localesKeys?.FOOTER_POWERED_BY)}
            </Typography>
            <a
              href="https://aparoksh.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.companyLink}
            >
              <img
                src="https://res.cloudinary.com/daojypjrh/image/upload/v1772993116/logo-new_ggqwad_ztoend.svg"
                alt="Aparoksh Technologies"
                className={styles.companyLogo}
              />
              <span className={styles.companyName}>
                {t(localesKeys?.FOOTER_COMPANY_NAME)}
              </span>
            </a>
          </Box>
        </Box>
      </Box>
    </footer>
  );
};

export default Footer;
